import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/shared/lib/utils";

const statusPillVariants = cva(
  "inline-flex w-fit shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors [&>svg]:size-3 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        success: "bg-success/10 text-success dark:bg-success/20",
        warning: "bg-warning/10 text-warning dark:bg-warning/20",
        info: "bg-blue-500/10 text-blue-700 dark:bg-blue-400/20 dark:text-blue-300",
        danger: "bg-destructive/10 text-destructive dark:bg-destructive/20",
        neutral: "bg-muted text-muted-foreground",
      },
      size: {
        default: "px-2.5 py-0.5 text-xs",
        sm: "px-2 py-px text-[11px] [&>svg]:size-2.5",
      },
    },
    defaultVariants: {
      variant: "neutral",
      size: "default",
    },
  },
);

export interface ColocaStatusPillProps
  extends React.ComponentPropsWithoutRef<"span">, VariantProps<typeof statusPillVariants> {
  /**
   * Optional leading icon that reinforces the semantic state — e.g. `<Check>` for a verified status. Keep it
   * for the states that benefit from a stronger visual cue; most pills read fine with color + label alone.
   */
  icon?: LucideIcon;
  /** Renders a small dot before the label instead of an icon. Ignored when `icon` is provided. */
  withDot?: boolean;
}

/**
 * Semantic state pill — the canonical way to display lifecycle states such as KYC verification (pending / in
 * review / verified / rejected / expired), operation results, or account statuses.
 *
 * Unlike `ColocaBadge` (categories, counters, tags), `ColocaStatusPill` is reserved for _states with semantic
 * meaning_: it always uses a soft background (~10% opacity) with a saturated text color, fully rounded
 * corners, and never solid color blocks.
 *
 * @summary soft semantic pill for lifecycle states (pending, verified, rejected, …)
 */
export const ColocaStatusPill = React.forwardRef<HTMLSpanElement, ColocaStatusPillProps>(
  function ColocaStatusPill({ variant, size, icon: Icon, withDot, className, children, ...rest }, ref) {
    return (
      <span ref={ref} className={cn(statusPillVariants({ variant, size }), className)} {...rest}>
        {Icon ? (
          <Icon aria-hidden="true" />
        ) : withDot ? (
          <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
        ) : null}
        {children}
      </span>
    );
  },
);
